import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import ThemeSwitch from './theme-switch';

export function NavBar({ items, className }) {
  const [hoveredItem, setHoveredItem] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Debounce hover state changes to prevent rapid switching
  const handleMouseEnter = (itemName) => {
    setHoveredItem(itemName);
  };
  
  const handleMouseLeave = () => {
    // Small delay to prevent flickering between navlinks
    setTimeout(() => {
      setHoveredItem('');
    }, 50);
  };

  // Primary items for mobile bottom nav
  const primaryItems = items.slice(0, 4); // Home, About, Projects, Blogs
  const secondaryItems = items.slice(4); // Snippets, Publications, Contact, Resume

  const handleItemClick = (item) => {
    if (item.url.startsWith('#')) {
      // Handle hash navigation
      const element = document.querySelector(item.url);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Handle regular navigation with React Router
      navigate(item.url);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false); // Close menu on navigation
  };

  return (
    <>
      {/* Desktop Navigation */}
      <motion.nav
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={cn(
          "hidden md:block fixed top-0 left-0 right-0 z-50",
          "bg-black/30 backdrop-blur-xl border-b border-white/20",
          "shadow-2xl",
          className
        )}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between py-4">
            <div className="flex-1" />
            <div className="flex items-center space-x-2 relative">
              {items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.url;
                const isHovered = hoveredItem === item.name;
                
                return (
                  <motion.button
                    key={item.name}
                    onClick={() => handleItemClick(item)}
                    onMouseEnter={() => handleMouseEnter(item.name)}
                    onMouseLeave={handleMouseLeave}
                    data-nav-item={item.name}
                                    className={cn(
                  "relative flex items-center justify-center space-x-2 px-4 py-2.5 rounded-xl",
                  "text-sm font-medium transition-all duration-500 ease-out",
                  "focus:outline-none cursor-default min-w-[120px]",
                  item.name === 'Resume' 
                    ? "font-semibold resume-nav-item" 
                    : isActive || isHovered
                      ? "text-white light:text-black"
                      : "text-white/70 hover:text-white light:text-slate-700 light:hover:text-black"
                )}
                style={{ cursor: 'default' }}
              >
                {/* Text content */}
                <div className="relative flex items-center space-x-2">
                  {/* Icon */}
                  {Icon && (
                    <Icon 
                      size={18} 
                      className={cn(
                        "relative z-10",
                        item.name === 'Resume' ? "" : "text-current"
                      )}
                    />
                  )}
                  
                  {/* Text with underline effect */}
                  <span className="relative z-10">
                    {item.name}
                    {/* Tubelight effect under text only */}
                    <AnimatePresence mode="wait">
                      {(isActive || isHovered) && (
                        <motion.div
                          layoutId={`navbar-underline-desktop-${item.name}`}
                          initial={{ opacity: 0, scaleX: 0, y: 2 }}
                          animate={{ 
                            opacity: 1, 
                            scaleX: 1,
                            y: 0,
                            boxShadow: [
                              "0 0 10px rgba(255, 255, 255, 0.4)",
                              "0 0 20px rgba(255, 255, 255, 0.6)",
                              "0 0 10px rgba(255, 255, 255, 0.4)"
                            ]
                          }}
                          exit={{ opacity: 0, scaleX: 0, y: 2 }}
                          transition={{ 
                            duration: 0.3,
                            ease: [0.4, 0, 0.2, 1],
                            scaleX: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                            boxShadow: {
                              repeat: Infinity,
                              duration: 2,
                              ease: "easeInOut"
                            }
                          }}
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-white rounded-full"
                        />
                      )}
                    </AnimatePresence>
                  </span>
                </div>




                  </motion.button>
                );
              })}
            </div>
            <div className="flex-1 flex justify-end">
              <ThemeSwitch />
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Bottom Navigation */}
      <motion.nav
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={cn(
          "md:hidden fixed bottom-0 left-0 right-0 z-50",
          "bg-black/30 backdrop-blur-xl border-t border-white/20",
          "shadow-2xl",
          className
        )}
      >
        <div className="flex items-center justify-between gap-1 px-2 py-3">
          {/* Primary Items */}
          {primaryItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.url;
            
            return (
              <motion.button
                key={item.name}
                onClick={() => handleItemClick(item)}
                className={cn(
                  "relative flex flex-col items-center justify-center space-y-1 px-2 py-2 rounded-xl",
                  "text-xs font-medium transition-all duration-500 ease-out",
                  "focus:outline-none cursor-default min-w-[60px] min-h-[56px]",
                  isActive
                    ? "text-white"
                    : "text-white/70 hover:text-white"
                )}
                style={{ cursor: 'default' }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Icon */}
                <Icon size={20} className="relative z-10" />
                
                {/* Text with underline effect */}
                <span className="relative z-10 text-[10px] leading-none">
                  {item.name}
                  {/* Tubelight effect under text only */}
                  <AnimatePresence mode="wait">
                    {isActive && (
                      <motion.div
                        layoutId={`navbar-underline-mobile-${item.name}`}
                        initial={{ opacity: 0, scaleX: 0, y: 1 }}
                        animate={{ 
                          opacity: 1, 
                          scaleX: 1,
                          y: 0,
                          boxShadow: [
                            "0 0 8px rgba(255, 255, 255, 0.4)",
                            "0 0 15px rgba(255, 255, 255, 0.6)",
                            "0 0 8px rgba(255, 255, 255, 0.4)"
                          ]
                        }}
                        exit={{ opacity: 0, scaleX: 0, y: 1 }}
                        transition={{ 
                          duration: 0.3,
                          ease: [0.4, 0, 0.2, 1],
                          scaleX: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                          boxShadow: {
                            repeat: Infinity,
                            duration: 2,
                            ease: "easeInOut"
                          }
                        }}
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-white rounded-full"
                      />
                    )}
                  </AnimatePresence>
                </span>
 

              </motion.button>
            );
          })}

			{/* Theme Switch removed on mobile to keep hamburger visible */}

          {/* Hamburger Menu Button */}
          <motion.button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={cn(
              "relative flex flex-col items-center space-y-1 px-3 py-2 rounded-xl",
              "text-xs font-medium transition-all duration-300",
              "focus:outline-none",
              isMenuOpen
                ? "text-white"
                : "text-white/70 hover:text-white"
            )}
            whileTap={{ scale: 0.95 }}
          >
            {/* Tubelight effect for menu button */}
            <AnimatePresence>
              {isMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ 
                    opacity: 1, 
                    scale: 1,
                    boxShadow: [
                      "0 0 15px rgba(255, 255, 255, 0.4)",
                      "0 0 25px rgba(255, 255, 255, 0.6)",
                      "0 0 15px rgba(255, 255, 255, 0.4)"
                    ]
                  }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ 
                    duration: 0.3,
                    boxShadow: {
                      repeat: Infinity,
                      duration: 2,
                      ease: "easeInOut"
                    }
                  }}
                  className="absolute inset-0 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20"
                />
              )}
            </AnimatePresence>

            {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
            <span className="text-[10px] leading-none">More</span>
          </motion.button>
        </div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              transition={{ duration: 0.3 }}
              className="absolute bottom-full left-0 right-0 bg-black/95 backdrop-blur-xl border-t border-white/30 shadow-2xl"
              style={{ background: 'rgba(10, 10, 10, 0.98)' }}
            >
              <div className="grid grid-cols-2 gap-3 p-6">
                {secondaryItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.url;
                  
                  return (
                    <motion.button
                      key={item.name}
                      onClick={() => handleItemClick(item)}
                      className={cn(
                        "relative flex items-center justify-start space-x-3 p-4 rounded-xl",
                        "text-sm font-medium transition-all duration-500 ease-out",
                        "focus:outline-none border border-white/10 cursor-default",
                        "bg-white/5 hover:bg-white/10 min-h-[60px]",
                        item.name === 'Resume'
                          ? "font-semibold border-white/30"
                          : isActive
                            ? "text-white bg-white/15 border-white/30"
                            : "text-white/80 hover:text-white hover:border-white/20"
                      )}
                      style={{ cursor: 'default' }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <Icon size={18} className={cn(
                        item.name === 'Resume' ? "" : "text-white/90"
                      )} />
                      <span className={cn(
                        "relative",
                        item.name === 'Resume' ? "font-semibold" : "text-white/90"
                      )}>
                        {item.name}
                        {/* Tubelight effect under text only */}
                        <AnimatePresence mode="wait">
                          {isActive && (
                            <motion.div
                              layoutId={`navbar-underline-menu-${item.name}`}
                              initial={{ opacity: 0, scaleX: 0, y: 1 }}
                              animate={{ 
                                opacity: 1, 
                                scaleX: 1,
                                y: 0,
                                boxShadow: [
                                  "0 0 8px rgba(255, 255, 255, 0.4)",
                                  "0 0 15px rgba(255, 255, 255, 0.6)",
                                  "0 0 8px rgba(255, 255, 255, 0.4)"
                                ]
                              }}
                              exit={{ opacity: 0, scaleX: 0, y: 1 }}
                              transition={{ 
                                duration: 0.3,
                                ease: [0.4, 0, 0.2, 1],
                                scaleX: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                                boxShadow: {
                                  repeat: Infinity,
                                  duration: 2,
                                  ease: "easeInOut"
                                }
                              }}
                              className="absolute bottom-0 left-0 right-0 h-0.5 bg-white rounded-full"
                            />
                          )}
                        </AnimatePresence>
                      </span>

                      {isActive && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full shadow-lg shadow-green-400/50"
                        />
                      )}
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
}
